import { client } from "@/client/client.gen";

/**
 * Konfiguration des generierten API-Clients.
 *
 * Must run once at startup, before the first request goes out.
 */

/** Set when the platform role is viewing a tenant. */
let viewAsTenantId: string | null = null;

export function setViewAsTenantId(id: string | null) {
  viewAsTenantId = id;
}

export function getViewAsTenantId(): string | null {
  return viewAsTenantId;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: { path: string[]; message: string }[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function configureApiClient(): void {
  client.setConfig({
    // No prefix: the OpenAPI spec already contains `/api` because
    // `setGlobalPrefix` runs before `createDocument`. An additional baseUrl
    // would produce `/api/api/...`.
    baseUrl: "",
    // Same origin, so the browser sends the session cookie automatically and
    // no token has to live in localStorage.
    credentials: "same-origin",
    throwOnError: true,
  });

  // The platform role's tenant context rides on every request. Without this
  // interceptor the platform view breaks silently, because the backend would
  // not know which tenant is meant.
  client.interceptors.request.use((request) => {
    if (viewAsTenantId) {
      request.headers.set("x-view-as-tenant", viewAsTenantId);
    }
    return request;
  });

  // Normalise error responses so the interface does not need to know the
  // backend's error shape in every place.
  client.interceptors.error.use((error, response) => {
    const body = error as {
      message?: string | string[];
      errors?: { path: string[]; message: string }[];
    } | null;

    const message = Array.isArray(body?.message)
      ? body.message.join(", ")
      : (body?.message ?? response?.statusText ?? "Unbekannter Fehler");

    return new ApiError(response?.status ?? 0, message, body?.errors);
  });
}

/**
 * Der konkrete Grund hinter einem Fehler der API, oder `null`.
 *
 * Bei einer Verletzung des Schemas ist `message` nur „Validation failed" — der
 * Grund steht in `issues`, je verletzter Regel einer. Ohne diese Auflösung
 * bekommt der Nutzer eine allgemeine Meldung und darf selbst suchen, welches
 * Feld gemeint war.
 *
 * Absichtlich `null` und kein Ersatztext: welcher Satz im Zweifel dasteht,
 * entscheidet die Stelle, die den Fehler anzeigt.
 */
export function apiFehlerDetail(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.issues?.length) {
    // Doppelte Meldungen kommen vor: dasselbe Limit an mehreren Feldern.
    const texte = [...new Set(error.issues.map((i) => i.message))].filter(
      Boolean,
    );
    if (texte.length) return texte.join(" ");
  }
  return error.message && error.message !== "Validation failed"
    ? error.message
    : null;
}
